import { type GetAssetType, VideoData } from '@lumen5/luminary';

import { checkpointVideo } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    addSceneWithDesignOperation,
    moveOtherOverlaysAndCreateOverlayOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import { saveRawLuminary } from 'js/src/luminary/utils';

export type HtmlAsset = GetAssetType<typeof VideoData.HTML_ASSET_TYPE>;

export interface AddHtmlAssetResult {
    sceneId: string;
    assetId: string;
}

/** Add an HtmlAsset filling the scene (or reuse the existing one) and return its ids. */
export const addHtmlAssetToSceneOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, initialHtml = ''): AddHtmlAssetResult | null => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return null;
        }

        // Only one dynamic graphic per scene — reuse the existing HtmlAsset if there is one.
        const existing = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0];
        if (existing) {
            return { sceneId: scene.id, assetId: existing.id };
        }

        let result: AddHtmlAssetResult | null = null;
        luminary.batchRun(() => {
            const asset = scene.addAsset(VideoData.HTML_ASSET_TYPE);
            const { width, height } = luminary.referenceResolution;
            asset.html = initialHtml;
            asset.width = width;
            asset.height = height;
            asset.transformation.position.x = width / 2;
            asset.transformation.position.y = height / 2;
            asset.transformation.anchor.x = 0.5;
            asset.transformation.anchor.y = 0.5;
            result = { sceneId: scene.id, assetId: asset.id };
        });
        return result;
    },
);

/**
 * Turn an existing scene into a blank "motion graphics scene": wipe everything a design added, then
 * attach a single empty HtmlAsset (html === '' — the durable "awaiting prompt" marker). Works for both a
 * freshly-added standalone scene and a newly-created overlay: `scene.duration` is a scene-level
 * property, so clearing the scene's children preserves it (an overlay keeps the span it was created
 * with), and the HtmlAsset fills the whole scene rather than depending on the subscene.
 */
export const blankifySceneForCodeoOperation = createLuminaryOperation(
    ({ luminary, call }, sceneId: string): AddHtmlAssetResult | null => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return null;
        }
        luminary.batchRun(() => {
            // Wipe everything a design added (base assets, stickers, subscenes, transitions, SFX),
            // then re-add the single subscene a scene needs.
            scene.clear();
            scene.addSubscene();
        });
        return call(addHtmlAssetToSceneOperation, sceneId, '');
    },
);

export interface AddCodeoSceneResult {
    sceneId: string;
    sceneGroupId: string;
    assetId: string;
}

/**
 * Create a blank "motion graphics scene" from scratch: add a scene with the template's default
 * design, then blank it into a codeo scene awaiting a generation prompt.
 */
export const addCodeoSceneOperation = createLuminaryOperation(
    ({ luminary, dispatch, call }, targetIndex: number): AddCodeoSceneResult | null => {
        const { sceneId, sceneGroup } = call(addSceneWithDesignOperation, targetIndex);
        const htmlResult = call(blankifySceneForCodeoOperation, sceneId);
        if (!htmlResult) {
            return null;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered: false,
                parentActionGroup: uuid(),
            }),
        );

        return { sceneId, sceneGroupId: sceneGroup.id, assetId: htmlResult.assetId };
    },
);

export const createCodeoOverlayOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        sceneGroupId: string | null,
        videoTimeStart: number | null,
        videoTimeEnd: number | null,
        baseSceneDesignId: string | undefined,
    ): AddHtmlAssetResult | null => {
        if (
            sceneGroupId === null ||
            videoTimeStart === null ||
            videoTimeEnd === null ||
            videoTimeStart < 0 ||
            videoTimeEnd < 0 ||
            !baseSceneDesignId
        ) {
            return null;
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return null;
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const transcriptStartTime = translateVideoTimeToTranscriptTime(
            videoTimeStart,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const transcriptEndTime = translateVideoTimeToTranscriptTime(
            videoTimeEnd,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );

        return call(
            createCodeoOverlayAtTranscriptRangeOperation,
            sceneGroupId,
            transcriptStartTime,
            transcriptEndTime,
            baseSceneDesignId,
        );
    },
);

/**
 * Create a codeo overlay across an already-resolved transcript-time range. Callers that start from a
 * word selection (the agent tools, via `resolveOverlayRange`) are already in transcript time; only
 * the UI, which works in video time, needs the translation `createCodeoOverlayOperation` does.
 */
export const createCodeoOverlayAtTranscriptRangeOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        sceneGroupId: string,
        transcriptStartTime: number,
        transcriptEndTime: number,
        baseSceneDesignId: string,
    ): AddHtmlAssetResult | null => {
        const parentActionGroup = uuid();
        const newSceneId = call(
            moveOtherOverlaysAndCreateOverlayOperation,
            sceneGroupId,
            transcriptStartTime,
            transcriptEndTime,
            baseSceneDesignId,
            false,
            parentActionGroup,
            false,
            [],
        );
        if (!newSceneId) {
            return null;
        }

        const htmlResult = call(blankifySceneForCodeoOperation, newSceneId);
        if (!htmlResult) {
            return null;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered: false,
                parentActionGroup,
            }),
        );

        return htmlResult;
    },
);

/** Set the composition HTML on an existing HtmlAsset; returns whether the asset was found. */
export const setHtmlAssetHtmlOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, assetId: string, html: string): boolean => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAssetById(assetId) as HtmlAsset | undefined;
        if (!asset) {
            return false;
        }
        luminary.batchRun(() => {
            asset.html = html;
        });
        return true;
    },
);
